import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import { useSerialNumbers, useSerialStats, useCreateSerial, useTransferSerial } from '@/hooks/useSerialNumbers';
import { useLocations } from '@/hooks/useLocations';
import { useItems } from '@/hooks/useItems';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Package,
  Search,
  Plus,
  ArrowRightLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Truck,
} from 'lucide-react';
import { format } from 'date-fns';

const statusColors: Record<string, string> = {
  available: 'bg-green-100 text-green-800',
  sold: 'bg-blue-100 text-blue-800',
  used: 'bg-purple-100 text-purple-800',
  returned: 'bg-yellow-100 text-yellow-800',
  damaged: 'bg-red-100 text-red-800',
  in_transit: 'bg-gray-100 text-gray-800',
};

export default function SerialNumbersPage() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [selectedSerial, setSelectedSerial] = useState<any>(null);

  const { data: serials = [], isLoading } = useSerialNumbers({
    search: searchQuery,
    status: statusFilter || undefined,
    page_size: 50,
  });
  const { data: stats } = useSerialStats();
  const { data: locations = [] } = useLocations();
  const { data: itemsData } = useItems({ limit: 100 });

  const createMutation = useCreateSerial();
  const transferMutation = useTransferSerial();

  const createFields: FormField[] = [
    { name: 'item_id', label: 'Item', type: 'select', required: true,
      options: itemsData?.data?.map(i => ({ value: i.item_id, label: `${i.sku} - ${i.name}` })) || [] },
    { name: 'serial_number', label: 'Serial Number', type: 'text', required: true },
    { name: 'location_id', label: 'Location', type: 'select', required: true,
      options: locations.map(l => ({ value: l.location_id, label: l.name })) },
    { name: 'unit_cost', label: 'Unit Cost', type: 'number' },
    { name: 'warranty_start_date', label: 'Warranty Start', type: 'date' },
    { name: 'warranty_end_date', label: 'Warranty End', type: 'date' },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ];

  const handleCreate = async (data: any) => {
    try {
      await createMutation.mutateAsync(data);
      toast({ title: 'Serial number created successfully' });
      setIsCreateModalOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleTransfer = async (toLocationId: string) => {
    if (!selectedSerial) return;
    try {
      await transferMutation.mutateAsync({
        serialId: selectedSerial.serial_id,
        toLocationId,
      });
      toast({ title: 'Serial number transferred successfully' });
      setIsTransferModalOpen(false);
      setSelectedSerial(null);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const columns = [
    { accessorKey: 'serial_number', header: 'Serial Number' },
    { accessorKey: 'item_name', header: 'Item' },
    { accessorKey: 'location_name', header: 'Location' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: any) => (
        <Badge className={statusColors[row.status] || 'bg-gray-100'}>
          {row.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'warranty_end_date',
      header: 'Warranty Expires',
      cell: ({ row }: any) => row.warranty_end_date ? format(new Date(row.warranty_end_date), 'dd MMM yyyy') : '-',
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: any) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedSerial(row);
              setIsTransferModalOpen(true);
            }}
          >
            <ArrowRightLeft className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Serial Number Tracking</h1>
          <p className="text-muted-foreground">Track individual items with unique serial numbers</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Serial Number
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Serials</p>
                <p className="text-2xl font-bold">{stats?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Available</p>
                <p className="text-2xl font-bold">{stats?.by_status?.available || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Warranty Expiring</p>
                <p className="text-2xl font-bold">{stats?.warranty_expiring_30_days || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Transit</p>
                <p className="text-2xl font-bold">{stats?.by_status?.in_transit || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search serial number, item..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="sold">Sold</option>
              <option value="used">Used</option>
              <option value="returned">Returned</option>
              <option value="damaged">Damaged</option>
              <option value="in_transit">In Transit</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <DataTable columns={columns} data={serials} />
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <ModalForm
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
        fields={createFields}
        title="Add Serial Number"
        isLoading={createMutation.isPending}
      />

      {/* Transfer Modal */}
      <Dialog open={isTransferModalOpen} onOpenChange={setIsTransferModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Serial Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{selectedSerial?.serial_number}</p>
              <p className="text-sm text-muted-foreground">{selectedSerial?.item_name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Transfer to Location</label>
              <select
                className="w-full px-4 py-2 border rounded-lg"
                onChange={(e) => handleTransfer(e.target.value)}
              >
                <option value="">Select location...</option>
                {locations
                  .filter((l) => l.location_id !== selectedSerial?.location_id)
                  .map((l) => (
                    <option key={l.location_id} value={l.location_id}>
                      {l.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
